import type { MerchantApiClient } from "../../types.js";
import { dispatchMerchantTelemetry } from "../../shared/telemetry.js";
import { createMappingsApi, type MappingsApi } from "./api.js";
import type {
  ManualMappingDraft,
  MappingDecisionDraft,
  MappingFilters,
  MappingPage,
  ProductMapping,
} from "./models.js";

export interface MappingsTelemetry {
  track(eventName: string, payload: Record<string, string | number | boolean | undefined>): void;
}

export interface MappingsService {
  createMapping(draft: ManualMappingDraft): Promise<ProductMapping>;
  loadMapping(productId: string, mappingId: string): Promise<ProductMapping>;
  loadMappings(filters: MappingFilters): Promise<MappingPage>;
  patchMapping(mapping: ProductMapping, draft: MappingDecisionDraft): Promise<ProductMapping>;
}

export interface MappingsServiceOptions {
  api?: MappingsApi;
  apiClient?: MerchantApiClient;
  idempotencyKeyFactory?: () => string;
  telemetry?: MappingsTelemetry;
}

export function createMappingsService(options: MappingsServiceOptions): MappingsService {
  const api = options.api ?? createApiFromClient(options.apiClient);
  const telemetry = options.telemetry ?? browserTelemetry();
  const idempotencyKeyFactory = options.idempotencyKeyFactory ?? (() => crypto.randomUUID());

  return {
    createMapping(draft) {
      return track(
        telemetry,
        "merchant_mapping_created",
        () => api.createMapping(draft, idempotencyKeyFactory()),
        { productId: draft.platformProductId },
      );
    },
    loadMapping(productId, mappingId) {
      return track(
        telemetry,
        "merchant_mapping_detail_loaded",
        () => api.getMapping(productId, mappingId),
        { productId, mappingId },
      );
    },
    loadMappings(filters) {
      return track(telemetry, "merchant_mappings_loaded", () => api.listMappings(filters), {
        status: filters.status,
        productId: filters.productId ?? filters.platformProductId,
      });
    },
    patchMapping(mapping, draft) {
      return track(
        telemetry,
        "merchant_mapping_reviewed",
        () => api.patchMapping(mapping, draft, idempotencyKeyFactory()),
        { mappingId: mapping.mappingId, action: draft.action },
      );
    },
  };
}

async function track<T>(
  telemetry: MappingsTelemetry,
  eventName: string,
  operation: () => Promise<T>,
  payload: Record<string, string | number | boolean | undefined>,
): Promise<T> {
  const startedAt = performance.now();
  try {
    const result = await operation();
    telemetry.track(eventName, {
      ...payload,
      result: "success",
      durationMs: Math.round(performance.now() - startedAt),
    });
    return result;
  } catch (error: unknown) {
    telemetry.track(eventName, {
      ...payload,
      result: "failure",
      durationMs: Math.round(performance.now() - startedAt),
      errorName: error instanceof Error ? error.name : "unknown",
    });
    throw error;
  }
}

function createApiFromClient(client: MerchantApiClient | undefined): MappingsApi {
  if (client === undefined) {
    throw new Error("Mappings service requires an API client.");
  }

  return createMappingsApi(client);
}

function browserTelemetry(): MappingsTelemetry {
  return {
    track(eventName, payload) {
      dispatchMerchantTelemetry(eventName, payload);
    },
  };
}
